import { debounce } from "lodash";
import { Field, Form } from "react-final-form";

import { ICommonColumn } from "./levels/templates/HandBooksTemplates";
import useHandBooksTemplatesRow from "./useHandBooksTemplatesRow";
import TableReusableRow, { TableReusableCell } from "shared/ui/dataDisplay/TableReusable/TableReusableRow";
import Checkbox from "shared/ui/inputs/Checkbox/Checkbox";
import InputBase, { INPUT_BASE_VARIANTS } from "shared/ui/inputs/InputBase";
import RowGrid from "shared/ui/layout/RowGrid/RowGrid";

import { composeFieldValidators, minLength, required } from "utils/formHelpers/validations";

import styles from "./HandBooksTemplatesRow.module.scss";

interface IProps<TItem extends Record<string, any>> {
  item: TItem;
  colPercents: number[];
  formColPercents: number[];
  columns: ICommonColumn<TItem>[];
  nameFieldPlaceholder: string;
  onRowClick: () => void;
}

const HandBooksTemplatesRow = <TItem extends Record<string, any>>({
  item,
  colPercents,
  formColPercents,
  columns,
  nameFieldPlaceholder,
  onRowClick,
}: IProps<TItem>) => {
  const { onSubmitEdit, isEditing, isChecked, onCheckItem } = useHandBooksTemplatesRow(item.id);
  if (isEditing) {
    return (
      <Form
        initialValues={item}
        onSubmit={onSubmitEdit}
        render={({ form }) => {
          const delayedFormSubmit = debounce(() => {
            form.submit();
          }, 200);
          return (
            <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
              <TableReusableRow containerClassName={styles.rowInEditing}>
                <RowGrid percents={formColPercents}>
                  <Checkbox onCheck={onCheckItem} checked={isChecked} />
                  <Field
                    name="name"
                    placeholder={nameFieldPlaceholder}
                    onEnter={() => {
                      form.submit();
                    }}
                    render={({ input, meta }) => (
                      <InputBase
                        input={{
                          ...input,
                          onChange: (...args) => {
                            input.onChange(...args);
                            delayedFormSubmit();
                          },
                        }}
                        meta={meta}
                        variant={INPUT_BASE_VARIANTS.SECONDARY}
                        classNameInput={styles.textInput}
                      />
                    )}
                    validate={composeFieldValidators(required(), minLength(1))}
                  />
                </RowGrid>
              </TableReusableRow>
            </form>
          );
        }}
      />
    );
  }

  return (
    <TableReusableRow onClick={onRowClick} className={styles.row}>
      <RowGrid percents={colPercents}>
        {columns.map((el, index) => (
          <TableReusableCell isCentered={el.isCentered} isNoBreak={el.isNoBreak} isRighted={el.isRighted} key={index}>
            {el.render(item)}
          </TableReusableCell>
        ))}
      </RowGrid>
    </TableReusableRow>
  );
};

export default HandBooksTemplatesRow;
